import { Info } from "lucide-react";
import { getRules, ruleOn, useRules } from "@/lib/rules";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";

export const METRIC_HINTS = {
  lp_history_csv: { title: "歷史資料（CSV 匯入）", body: "從 LINE OA 後台匯出的聊天紀錄 CSV。只有文字訊息，沒有加好友／選單點擊事件，所以只能補「過去」的對話分析。" },
  lp_realtime_api: { title: "即時對話（Messaging API）", body: "LINE 透過 Webhook 傳來的客人訊息，只用於對話分類、回覆狀態與健康檢查；好友數改用路徑 CSV 與 LINE 官方每日統計。" },
  effective_friends: { title: "目前有效好友數", body: "LINE 官方統計的好友數 − 封鎖數，取最新一天（T+1，資料截至昨天）。每天 10:30 自動抓取。" },
  lp_classify_progress: { title: "分類進度", body: "AI 逐筆判讀訊息，把客人分成冷／溫／熱單。已分類 ÷ 全部待分類 = 進度；還沒分類完之前，溫單、熱單數會偏低。" },
  lp_classify_mode: { title: "分類模式", body: "事件層級：每則訊息各自判讀。\nThread 層級：把同一位客人一段時間內的對話合起來判讀，較準、較省。\n已停用：不做 AI 分類。" },
  lp_tiers: { title: "冷／溫／熱單", body: "冷單：時間範圍內有任何活動的客人。\n溫單：冷單中至少有一則「非選單」訊息的客人。\n熱單：有明確訂房意圖（日期＋人數＋房型）的客人。\n三者是累積包含：熱單 ≤ 溫單 ≤ 冷單。" },
  lp_backfill: { title: "補分析歷史訊息（backfill）", body: "把已匯入但還沒分類的歷史訊息，排隊交給 AI 分類。可暫停、繼續、取消；碰到 LINE／AI 額度限制會自動暫停後再恢復。" },
  lp_runs: { title: "過去分析記錄", body: "每一次補分析都是一個 run。\n重試失敗項：只重跑分類失敗的訊息。\n回滾：把該次 run 的分類結果作廢（不刪資料），熱單狀態會重算。\n預跑：抽 50 筆試分類、不寫入，用來先看品質與成本。" },
  spend: "廣告消耗預算的總金額（含稅後），依選定日期範圍計算",
  impressions: {
    title: "曝光（Meta impressions）",
    body: "廣告出現在螢幕上的次數，同一人看多次會重複計算。",
  },
  reach: "看過你廣告的不重複人數，無論看了幾次都只算一次",
  clicks: {
    title: "點擊（全部）· API clicks",
    body: (
      "廣告上所有可點擊的互動，含連結、按讚、留言、分享、點粉專/大頭貼、展開圖片影片、查看更多；同一人點多次會重複計算。\n\n" +
      "意義：衡量廣告的整體互動熱度，不等於導流人數。"
    ),
  },
  link_clicks: {
    title: "連結點擊 · API inline_link_clicks",
    body: (
      "點擊廣告中導向目的地（加 LINE、網站、表單）的連結次數，同一人多次點擊會重複計算。\n\n" +
      "意義：最接近實際導流到 LINE 的行為。"
    ),
  },
  ctr: {
    title: "CTR（全部）· 點擊率",
    body: (
      "公式：點擊（全部）÷ 曝光 ×100%\n\n" +
      "意義：廣告吸引人點的程度。\n\n" +
      "顏色：這裡用的是 CTR（全部），低於 3% 紅字（偏低）、3%～5% 黃字（普通）、5% 以上正常。"
    ),
  },
  link_ctr: {
    title: "連結 CTR",
    body: (
      "公式：連結點擊 ÷ 曝光 ×100%\n\n" +
      "意義：廣告吸引人點進目的地（加 LINE）的程度。"
    ),
  },
  // TODO: cpc hint retained as dead code for easy restore (removed from UI per L4).
  cpc: {
    title: "CPC · 每次點擊成本",
    body: (
      "公式：花費 ÷ 點擊\n\n" +
      "資料來源：Meta Marketing API `spend` 與 `clicks` 欄位，平台自動計算。\n\n" +
      "關係：CPM 低 + CTR 高 → CPC 低。CPC 是 CPM 和 CTR 的綜合結果。"
    ),
  },
  cpm: {
    title: "CPM · 千次曝光成本",
    body: (
      "公式：花費 ÷ 曝光 ×1000\n\n" +
      "意義：每 1,000 次曝光的成本，反映買流量貴不貴。"
    ),
  },
  cpa: {
    title: "CPA（每次轉換成本）",
    body: (
      "公式：花費 ÷ Meta 回報的轉換數\n\n" +
      "意義：每取得一次 Meta 定義轉換的成本（非 LINE 加好友成本）。\n\n" +
      "轉換數 = 訊息對話開啟（messaging_conversation_started_7d）+ 購買類事件（同一筆購買只算一次）。真實加好友成本請看「LINE 洞察」頁。"
    ),
  },
  real_cpa: {
    title: "實際加好友成本（LINE 口徑）",
    body: (
      "公式：Meta 花費 ÷ LINE 實際新朋友數\n\n" +
      "新朋友數來源：\n" +
      "• LINE webhook 的 follow 事件\n" +
      "• 每週手動輸入的管道數據\n\n" +
      "和 Meta 的 CPA 差在哪：\n" +
      "Meta 的 CPA 依據 Meta 平台回報的轉換事件（含 Messenger 對話、購買），不是 LINE 加好友。這張卡的數字才是以 LINE 真實加好友當分母的成本。\n\n" +
      "精度限制：\n" +
      "只能到『場域』層級，無法到單一活動或單一素材。因為我們是用場域名稱比對廣告，再去對應該場域所有新朋友。"
    ),
  },
  roas: {
    title: "ROAS · 廣告投資報酬率",
    body: (
      "公式：營業額 ÷ 廣告花費\n\n" +
      "燈號：綠 ≥35x / 黃 20–35x / 紅 <20x；沒有營業額資料顯示灰色「無營收資料」。"
    ),
  },
  results: {
    title: "成果（Meta Results）",
    body: "成果：依廣告目標計算的主要成效次數（例如：開始對話、連結點擊）。不同目標的成果種類不同，加總時請留意。",
  },
  cost_per_result: {
    title: "每次成果成本",
    body: "公式：花費 ÷ 成果\n\n成果依各廣告組合的最佳化目標計算（例如：開始對話）。",
  },
  friend_adds: {
    title: "加好友",
    body: "加好友「次數」（與 LINE OA 後台「此期間加入好友者」同口徑，封鎖後重新加入會再算一次，不是不重複人數）。\n\n每個場域每天優先採用 LINE OA 加好友路徑 CSV 的「加入」數；最後一次 CSV 之後到昨天，用 LINE 官方每日統計（好友數差值）暫估，沒有路徑明細；再沒有才用手動輸入。資料只到昨天（T+1），同一天絕不重複加總兩種來源。Webhook 加好友事件不再計入。",
  },
  friend_blocked: {
    title: "封鎖",
    body: "與加好友同樣的來源優先順序：CSV「封鎖」→ LINE 官方每日統計（封鎖數差值，為扣掉解除封鎖的淨值，暫估）→ 手動輸入。Webhook 封鎖事件不再計入。",
  },
  friend_net: { title: "淨增好友", body: "公式：加好友 − 封鎖" },
  friend_block_rate: { title: "封鎖率", body: "公式：封鎖 ÷ 加好友 ×100%" },
  frequency: "平均每個人看過你廣告幾次，過高（>3）可能廣告疲乏",
  real_human: "扣除選單按鈕互動後，真人輸入文字的訊息數量",
  inquiry: "真人訊息中詢問住宿、空房、價格、方案的數量",
  hot: "問房訊息中同時包含『明確時間 + 人數 + 物件名稱』三要素的高意圖訊息",
  followers: "LINE OA 當前好友總數（LINE Insight API 回傳的最新值）",
  blocks: "封鎖你 LINE OA 的使用者數量",
  targeted_reaches: "可被訊息觸及的用戶（已加好友未封鎖）",
};

// R1 — global ROAS traffic light (thresholds from /settings/rules)
export const roasLegend = () => { const v = getRules().values; return `綠 ≥${v.roas_green_min}x / 黃 ${v.roas_yellow_min}–${v.roas_green_min}x / 紅 <${v.roas_yellow_min}x`; };
export const ROAS_LEGEND = "綠 ≥35x / 黃 20–35x / 紅 <20x";

export function roasLight(roas) {
  if (roas == null || Number.isNaN(Number(roas))) {
    return { key: "none", label: "無營收資料", dot: "bg-slate-300", cls: "border-slate-200 bg-slate-50 text-slate-500" };
  }
  const v = Number(roas);
  const R = getRules().values;
  if (v >= R.roas_green_min) return { key: "green", label: "綠燈", dot: "bg-emerald-500", cls: "border-emerald-200 bg-emerald-50 text-emerald-700" };
  if (v >= R.roas_yellow_min) return { key: "yellow", label: "黃燈", dot: "bg-amber-400", cls: "border-amber-200 bg-amber-50 text-amber-700" };
  return { key: "red", label: "紅燈", dot: "bg-rose-500", cls: "border-rose-200 bg-rose-50 text-rose-700" };
}

// J2 — CPA / CTR text colours (人客多內部規則, editable in /settings/rules)
export function cpaLevel(cpa) {
  if (cpa == null || Number.isNaN(Number(cpa)) || !ruleOn("cpa_color")) return null;
  const R = getRules().values;
  return cpa <= R.cpa_green_max ? "green" : cpa <= R.cpa_yellow_max ? "yellow" : "red";
}
const TONE = { green: "text-emerald-600", yellow: "text-amber-600", red: "text-rose-600" };
export const cpaTone = (cpa) => TONE[cpaLevel(cpa)] || "";
export const ctrTone = (ctr) => {
  if (ctr == null || !ruleOn("ctr")) return "";
  const R = getRules().values;
  return ctr < R.ctr_red_below ? "text-rose-600" : ctr < R.ctr_yellow_below ? "text-amber-600" : "";
};
export const cpaRuleText = () => { const R = getRules().values; return `人客多內部規則：≤${R.cpa_green_max} 綠 / ${R.cpa_green_max + 1}–${R.cpa_yellow_max} 黃 / ≥${R.cpa_yellow_max + 1} 紅`; };
const RULE_LINES = {
  cpa: cpaRuleText,
  ctr: () => { const R = getRules().values; return `人客多內部規則（CTR 全部）：<${R.ctr_red_below}% 紅 / <${R.ctr_yellow_below}% 黃`; },
  roas: () => `人客多內部規則：${roasLegend()}`,
};

export function CpaValue({ value, children, className = "", testid }) {
  useRules();
  const lvl = cpaLevel(value);
  if (value == null) return <span className={className} data-testid={testid}>{children ?? "—"}</span>;
  return (
    <HoverCard>
      <HoverCardTrigger asChild>
        <span className={`cursor-help ${TONE[lvl] || ""} ${className}`} data-testid={testid} data-cpa-level={lvl || "none"}>{children}</span>
      </HoverCardTrigger>
      <HoverCardContent side="top" align="end" className="w-auto bg-white border border-gray-200 text-slate-700 text-xs p-2.5 rounded-md shadow-xl" data-testid={testid ? `${testid}-rule` : undefined}>
        {cpaRuleText()}
      </HoverCardContent>
    </HoverCard>
  );
}

export function RevenueSourceChip({ source, testid }) {
  if (!source) return null;
  const label = source === "pms" ? "PMS" : source === "manual" ? "手填" : "PMS＋手填";
  const cls = source === "pms" ? "border-sky-200 bg-sky-50 text-sky-700" : source === "manual" ? "border-slate-200 bg-white text-slate-600" : "border-violet-200 bg-violet-50 text-violet-700";
  return <span className={`inline-flex items-center rounded border px-1.5 py-0 text-[10px] font-medium ${cls}`} data-testid={testid}>{label}</span>;
}

export function RoasBadge({ roas, size = "sm", showLabel = false, testid, prefix = "ROAS" }) {
  const l = roasLight(roas);
  const txt = size === "lg" ? "text-2xl font-bold" : "text-[11px]";
  return (
    <HoverCard openDelay={100} closeDelay={80}>
      <HoverCardTrigger asChild>
        <span
          className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 font-mono tabular-nums cursor-help ${l.cls} ${txt}`}
          data-testid={testid}
          data-roas-light={l.key}
        >
          <span className={`inline-block rounded-full ${size === "lg" ? "h-3 w-3" : "h-2 w-2"} ${l.dot}`} />
          {roas == null ? (prefix ? `${prefix} · 無營收資料` : "無營收資料") : `${prefix ? prefix + " " : ""}${Number(roas).toFixed(2)}x`}
          {showLabel && roas != null && <span className="font-sans text-[10px] opacity-80">{l.label}</span>}
        </span>
      </HoverCardTrigger>
      <HoverCardContent side="bottom" align="start" sideOffset={8} className="w-[240px] bg-white border border-gray-200 text-slate-700 text-xs p-3 rounded-md shadow-xl" data-testid={testid ? `${testid}-legend` : undefined}>
        <div className="font-semibold text-slate-900 mb-1">ROAS 燈號</div>
        <div>{roasLegend()}</div>
        <div className="text-slate-500 mt-1">ROAS = 營業額 ÷ 廣告花費；無營業額資料時顯示灰色。</div>
      </HoverCardContent>
    </HoverCard>
  );
}

// H2 — data-quality flags → plain-zh「⚠️ 注意」lines (no English codes)
export function qualityNotes(f = {}) {
  const out = [];
  if (f.no_data) out.push(`這段期間沒有資料，可能是尚未匯入或 API 未連線 → ${f.fix || "到「設定」匯入或檢查連線"}`);
  if (f.coverage_days != null && f.total_days && f.coverage_days > 0 && f.coverage_days < f.total_days)
    out.push(`只有 ${f.coverage_days}/${f.total_days} 天有資料，數字會偏低`);
  if (f.is_estimated) out.push(f.estimated_text || "部分日子用 LINE 官方每日統計暫估（T+1；封鎖是扣掉解除封鎖後的淨值），匯入路徑 CSV 後會自動改用 CSV");
  if (f.no_results) out.push("沒有成果，所以無法計算 CPA／每次成果成本");
  if (f.no_conversions) out.push("沒有轉換事件，所以無法計算 CPA");
  if (f.unattributed_spend > 0) out.push(`有 NT$${Math.round(f.unattributed_spend).toLocaleString()} 花費屬於「未歸屬」活動，沒算進 ROAS 分母 → 到 /meta-poc 把活動拖進場域`);
  if (f.updated_at) {
    const mins = Math.round((Date.now() - new Date(f.updated_at).getTime()) / 60000);
    if (mins >= (f.stale_after_min ?? 15)) {
      const t = new Date(f.updated_at);
      out.push(`資料更新於 ${String(t.getHours()).padStart(2, "0")}:${String(t.getMinutes()).padStart(2, "0")}，已超過 ${mins} 分鐘 → 可按「同步」更新`);
    }
  }
  if (f.revenue_source === "manual") out.push("營業額是手填的（PMS 未連線），請確認數字是否最新");
  if (f.revenue_missing) out.push("沒有營業額資料，無法計算 ROAS → 到「設定 › 營收」手填或連接 PMS");
  if (f.mixed_results) out.push("包含不同種類的成果（例如開始對話＋連結點擊），加總僅供參考");
  if (f.reply_inferred) out.push("回覆狀態是系統依對話推算的，可能和實際略有不同");
  return out;
}

export function QualityNotes({ flags, notes, testid }) {
  const list = [...(notes || []), ...qualityNotes(flags || {})];
  if (!list.length) return null;
  return (
    <div className="mt-2 space-y-1 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] leading-snug text-amber-800" data-testid={testid || "quality-notes"}>
      {list.map((t, i) => <div key={i}>⚠️ 注意：{t}</div>)}
    </div>
  );
}

// H1 — single hover/click info component: portaled, always on top, click toggles (trackpads), Esc closes
export function MetricLabel({ label, hintKey, className = "", testid, flags, notes, extra }) {
  const hint = METRIC_HINTS[hintKey];
  const hasWarn = !!(notes?.length || qualityNotes(flags || {}).length);
  if (!hint && !hasWarn && !extra) return <span className={className}>{label}</span>;
  const isStructured = typeof hint === "object" && hint !== null;
  return (
    <span className={`inline-flex items-center gap-1 ${className}`}>
      <span>{label}</span>
      <HoverCard>
        <HoverCardTrigger asChild>
          <button
            type="button"
            className={`inline-flex items-center justify-center cursor-help bg-transparent border-0 p-0 m-0 leading-none ${hasWarn ? "text-amber-600" : "text-slate-500 hover:text-slate-800"}`}
            data-testid={testid || `metric-info-${hintKey}`}
            aria-label={`${label} 說明`}
          >
            <Info className="h-3.5 w-3.5 flex-shrink-0" />
          </button>
        </HoverCardTrigger>
        <HoverCardContent
          side="bottom"
          align="start"
          sideOffset={8}
          className="w-auto bg-white border border-gray-200 text-slate-700 text-xs leading-relaxed p-3 rounded-md shadow-xl"
          data-testid={`${testid || `metric-info-${hintKey}`}-card`}
        >
          {hint && (isStructured ? (
            <div className="space-y-1.5">
              <div className="font-semibold text-slate-900">{hint.title}</div>
              <div className="whitespace-pre-line text-slate-600">{hint.body}</div>
            </div>
          ) : <span className="text-slate-700">{hint}</span>)}
          {extra}
          {RULE_LINES[hintKey] && <div className="mt-1.5 text-slate-500" data-testid={`${testid || `metric-info-${hintKey}`}-rule`}>{RULE_LINES[hintKey]()}</div>}
          <QualityNotes flags={flags} notes={notes} testid={`${testid || `metric-info-${hintKey}`}-notes`} />
        </HoverCardContent>
      </HoverCard>
    </span>
  );
}
